import React, { useEffect, useState } from 'react';
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Rating,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SentimentSatisfiedAltIcon from '@mui/icons-material/SentimentSatisfiedAlt';
import { getEncuestaSatisfaccion, postEncuestaSatisfaccion } from '../../services/apis';
import { ordenarOpcionesPorLabel, textoMayusculas } from '../../utils/utils';

export const EncuestaSatisfaccion = () => {
    const [loaded, setLoaded] = useState(false);
    const [trabajadores, setTrabajadores] = useState([]);
    const [trabajador, setTrabajador] = useState(null);
    const [puntuacion, setPuntuacion] = useState(0);
    const [comentario, setComentario] = useState('');
    const [enviando, setEnviando] = useState(false);
    const [mensaje, setMensaje] = useState('');
    const [error, setError] = useState('');
    const [accessDenied, setAccessDenied] = useState(false);

    const cargarEncuesta = async () => {
        setLoaded(false);
        setError('');
        setAccessDenied(false);

        const response = await getEncuestaSatisfaccion();

        if (!response?.success) {
            setAccessDenied(Boolean(response?.access_denied));
            setError(response?.message || 'No se pudo cargar la encuesta de satisfaccion.');
            setLoaded(true);
            return;
        }

        setTrabajadores(ordenarOpcionesPorLabel(response.data?.trabajadores || []));
        setLoaded(true);
    };

    useEffect(() => {
        cargarEncuesta();
    }, []);

    const enviarEncuesta = async () => {
        if (!trabajador?.value || !puntuacion) {
            setError('Selecciona un trabajador y una puntuacion para enviar la encuesta.');
            return;
        }

        setEnviando(true);
        setError('');
        setMensaje('');

        const response = await postEncuestaSatisfaccion({
            id_usuario_trabajador: trabajador.value,
            puntuacion,
            comentario,
        });

        setEnviando(false);

        if (!response?.success) {
            setError(response?.message || 'No se pudo enviar la encuesta.');
            return;
        }

        setMensaje('Gracias. Tu encuesta se ha enviado correctamente.');
        setTrabajador(null);
        setPuntuacion(0);
        setComentario('');
    };

    return (
        <Grid
            container
            direction="row"
            justifyContent="center"
            alignItems="flex-start"
            xs={12}
            sx={{ py: { xs: 2, md: 5 }, px: 1 }}
            className="fade-in-2"
        >
            <Grid item xs={12} sm={10} md={7} lg={5} xl={4}>
                <Card
                    sx={{
                        borderRadius: '8px',
                        border: '1px solid rgba(229, 57, 53, 0.16)',
                        boxShadow: '0 12px 28px rgba(0,0,0,0.08)',
                    }}
                >
                    <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                        <Stack spacing={2.4}>
                            <Box sx={{ textAlign: 'center' }}>
                                <SentimentSatisfiedAltIcon color="primary" sx={{ fontSize: 52 }} />
                                <Typography variant="h5" sx={{ fontWeight: 800, mt: 1 }}>
                                    Encuesta de satisfaccion
                                </Typography>
                                <Typography sx={{ color: 'text.secondary', mt: 0.5 }}>
                                    Valora el trabajo realizado por nuestro equipo.
                                </Typography>
                            </Box>

                            {!loaded ? (
                                <Box sx={{ minHeight: 160, display: 'grid', placeItems: 'center' }}>
                                    <CircularProgress />
                                </Box>
                            ) : accessDenied ? (
                                <Alert severity="warning">
                                    {error}
                                </Alert>
                            ) : (
                                <>
                                    {error && (
                                        <Alert severity="error">
                                            {error}
                                        </Alert>
                                    )}

                                    {mensaje && (
                                        <Alert severity="success">
                                            {mensaje}
                                        </Alert>
                                    )}

                                    <Autocomplete
                                        fullWidth
                                        options={trabajadores}
                                        value={trabajador}
                                        getOptionLabel={(option) => textoMayusculas(option?.label)}
                                        onChange={(_, newValue) => {
                                            setTrabajador(newValue);
                                            setError('');
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="Trabajador valorado" />
                                        )}
                                    />

                                    <Box>
                                        <Typography sx={{ fontWeight: 700, mb: 0.8 }}>
                                            Puntuacion
                                        </Typography>
                                        <Rating
                                            value={puntuacion}
                                            onChange={(_, newValue) => {
                                                setPuntuacion(newValue || 0);
                                                setError('');
                                            }}
                                            size="large"
                                        />
                                    </Box>

                                    <TextField
                                        label="Comentario opcional"
                                        value={comentario}
                                        onChange={(event) => setComentario(event.target.value)}
                                        multiline
                                        minRows={4}
                                        fullWidth
                                    />

                                    <Button
                                        variant="contained"
                                        color="primary"
                                        endIcon={<SendIcon />}
                                        onClick={enviarEncuesta}
                                        disabled={enviando}
                                        fullWidth
                                    >
                                        {enviando ? 'Enviando...' : 'Enviar encuesta'}
                                    </Button>
                                </>
                            )}
                        </Stack>
                    </CardContent>
                </Card>
            </Grid>
        </Grid>
    );
};
